import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft,
  Users,
  Settings,
  X,
  Check,
  Plus,
  Trash2,
  AlertTriangle,
  UserPlus,
  Clock,
  ShieldCheck,
  Ban,
  Power,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserManagement() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState(null);
  const [tempPermissions, setTempPermissions] = useState([]);
  
  // Add user modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUserData, setNewUserData] = useState({ name: '', email: '', role: 'production' });
  const [addingUser, setAddingUser] = useState(false);
  
  // Delete user modal state
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Approve modal state
  const [approvingUser, setApprovingUser] = useState(null);
  const [approvalRole, setApprovalRole] = useState('production');
  const [approvalCompanyIds, setApprovalCompanyIds] = useState([]);
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else if (response.status === 403) {
        toast.error('Access denied');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/permissions`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAllPermissions(data.permissions || []);
        setDefaultPermissions(data.default_permissions || {});
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/companies`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  }, []);

  const openApprovalModal = (u) => {
    setApprovingUser(u);
    setApprovalRole(u.role || 'production');
    setApprovalCompanyIds(u.company_ids || []);
  };

  const toggleApprovalCompany = (cid) => {
    setApprovalCompanyIds(prev =>
      prev.includes(cid) ? prev.filter(c => c !== cid) : [...prev, cid]
    );
  };

  const handleApproveUser = async () => {
    if (!approvingUser) return;
    setApprovalSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/users/${approvingUser.user_id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: approvalRole, company_ids: approvalCompanyIds })
      });
      if (response.ok) {
        toast.success(`Approved ${approvingUser.name}`);
        setApprovingUser(null);
        await fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve user');
      }
    } catch (error) {
      toast.error('Failed to approve user');
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const handleRejectUser = async (targetUser) => {
    if (!confirm(`Reject ${targetUser.name}? They will lose all access.`)) return;
    setUpdatingUserId(targetUser.user_id);
    try {
      const response = await fetch(`${API_URL}/api/users/${targetUser.user_id}/reject`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('User rejected');
        await fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reject user');
      }
    } catch (error) {
      toast.error('Failed to reject user');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleToggleActive = async (targetUser) => {
    const newActive = !(targetUser.is_active ?? true);
    if (!newActive && !confirm(`Disable ${targetUser.name}? They will lose access immediately.`)) return;
    setUpdatingUserId(targetUser.user_id);
    try {
      const response = await fetch(`${API_URL}/api/users/${targetUser.user_id}/active`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: newActive })
      });
      if (response.ok) {
        toast.success(newActive ? 'User enabled' : 'User disabled');
        await fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingUserId(null);
    }
  };

  useEffect(() => {
    if (!authLoading && user) {
      if (user.role !== 'manager') {
        toast.error('Access denied');
        navigate('/dashboard');
        return;
      }
      fetchUsers();
      fetchPermissions();
      fetchCompanies();
    }
  }, [authLoading, user, fetchUsers, fetchPermissions, fetchCompanies, navigate]);

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingUserId(userId);
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success('Role updated');
        // Refetch users to get updated permissions
        await fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const openPermissionsEditor = (targetUser) => {
    setEditingPermissions(targetUser.user_id);
    setTempPermissions(targetUser.effective_permissions || []);
  };

  const closePermissionsEditor = () => {
    setEditingPermissions(null);
    setTempPermissions([]);
  };

  const togglePermission = (permId) => {
    setTempPermissions(prev => 
      prev.includes(permId) 
        ? prev.filter(p => p !== permId)
        : [...prev, permId]
    );
  };

  const savePermissions = async () => {
    if (!editingPermissions) return;
    
    setUpdatingUserId(editingPermissions);
    try {
      const response = await fetch(`${API_URL}/api/users/${editingPermissions}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ permissions: tempPermissions })
      });

      if (response.ok) {
        toast.success('Permissions updated');
        await fetchUsers();
        closePermissionsEditor();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update permissions');
      }
    } catch (error) {
      toast.error('Failed to update permissions');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const resetToDefault = (role) => {
    setTempPermissions(defaultPermissions[role] || []);
  };

  // Add new user
  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserData.name.trim() || !newUserData.email.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAddingUser(true);
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newUserData)
      });

      if (response.ok) {
        toast.success('User added successfully');
        setShowAddModal(false);
        setNewUserData({ name: '', email: '', role: 'production' });
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add user');
      }
    } catch (error) {
      toast.error('Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!deletingUser) return;

    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/users/${deletingUser.user_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('User deleted successfully');
        setDeletingUser(null);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      production: 'bg-slate-100 text-slate-700 border-slate-200',
      lab: 'bg-amber-50 text-amber-700 border-amber-200',
      accounts: 'bg-blue-50 text-blue-700 border-blue-200',
      manager: 'bg-purple-50 text-purple-700 border-purple-200'
    };
    return classes[role] || classes.production;
  };

  const editingUser = users.find(u => u.user_id === editingPermissions);

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="user-management-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="h-10 w-10 p-0"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">User Management</h1>
              <p className="text-slate-500 mt-1">Manage user roles and permissions</p>
            </div>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>

        {/* Role Legend */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Default Role Permissions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getRoleBadgeClass('production')}`}>
                Production
              </span>
              <p className="text-xs text-slate-500 mt-1">Create tickets, raw materials, batch import</p>
            </div>
            <div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getRoleBadgeClass('lab')}`}>
                Lab
              </span>
              <p className="text-xs text-slate-500 mt-1">Create tickets, approve/reject batches</p>
            </div>
            <div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getRoleBadgeClass('accounts')}`}>
                Accounts
              </span>
              <p className="text-xs text-slate-500 mt-1">Create tickets, process stock entries</p>
            </div>
            <div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getRoleBadgeClass('manager')}`}>
                Manager
              </span>
              <p className="text-xs text-slate-500 mt-1">Full access, user management</p>
            </div>
          </div>
          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
            <strong>Note:</strong> After changing a user's role or permissions, they may need to refresh their browser or re-login to see the changes.
          </div>
          <p className="text-xs text-slate-400 mt-2">
            <Settings className="w-3 h-3 inline mr-1" />
            Click "Permissions" to customize access for individual users
          </p>
        </div>

        {/* Pending Users Section */}
        {users.filter(u => (u.status ?? 'approved') === 'pending').length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="pending-users-section">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-amber-600" />
              <h3 className="text-sm font-semibold text-amber-900">
                Pending Approvals ({users.filter(u => (u.status ?? 'approved') === 'pending').length})
              </h3>
            </div>
            <div className="space-y-2">
              {users.filter(u => (u.status ?? 'approved') === 'pending').map(u => (
                <div
                  key={u.user_id}
                  className="bg-white border border-amber-200 rounded-md p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  data-testid={`pending-user-${u.user_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-semibold text-slate-600">
                          {u.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{u.name}</p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => openApprovalModal(u)}
                      disabled={updatingUserId === u.user_id}
                      className="gap-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`approve-user-btn-${u.user_id}`}
                    >
                      <ShieldCheck className="w-4 h-4" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRejectUser(u)}
                      disabled={updatingUserId === u.user_id}
                      className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`reject-user-btn-${u.user_id}`}
                    >
                      <Ban className="w-4 h-4" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="users-list">
          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="table-header-row">
                  <th className="table-header-cell">User</th>
                  <th className="table-header-cell">Email</th>
                  <th className="table-header-cell">Role</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Permissions</th>
                  <th className="table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.filter(u => (u.status ?? 'approved') !== 'pending').map((u) => (
                  <tr key={u.user_id} className="table-row" data-testid={`user-row-${u.user_id}`}>
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-sm font-semibold text-slate-600">
                              {u.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          )}
                        </div>
                        <span className="font-medium text-slate-900">{u.name}</span>
                      </div>
                    </td>
                    <td className="table-cell text-slate-600">{u.email}</td>
                    <td className="table-cell">
                      {u.user_id === user?.user_id ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border capitalize ${getRoleBadgeClass(u.role)}`}>
                          {u.role}
                        </span>
                      ) : (
                        <Select
                          value={u.role}
                          onValueChange={(value) => handleRoleChange(u.user_id, value)}
                          disabled={updatingUserId === u.user_id}
                        >
                          <SelectTrigger className="h-9 w-32" data-testid={`role-select-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="production">Production</SelectItem>
                            <SelectItem value="lab">Lab</SelectItem>
                            <SelectItem value="accounts">Accounts</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="table-cell">
                      {(() => {
                        const st = u.status ?? 'approved';
                        const active = u.is_active ?? true;
                        if (st === 'rejected') return (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 border border-red-200" data-testid={`status-${u.user_id}`}>
                            <Ban className="w-3 h-3" /> Rejected
                          </span>
                        );
                        if (!active) return (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600 border border-slate-200" data-testid={`status-${u.user_id}`}>
                            <Power className="w-3 h-3" /> Disabled
                          </span>
                        );
                        return (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200" data-testid={`status-${u.user_id}`}>
                            <ShieldCheck className="w-3 h-3" /> Active
                          </span>
                        );
                      })()}
                    </td>
                    <td className="table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(u.effective_permissions || []).slice(0, 3).map(perm => (
                          <span key={perm} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                            {perm.replace('_', ' ')}
                          </span>
                        ))}
                        {(u.effective_permissions || []).length > 3 && (
                          <span className="text-xs text-slate-400">+{u.effective_permissions.length - 3} more</span>
                        )}
                        {u.permissions && (
                          <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">custom</span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      {u.user_id !== user?.user_id && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPermissionsEditor(u)}
                            className="h-8 text-xs"
                            data-testid={`edit-permissions-${u.user_id}`}
                          >
                            <Settings className="w-3 h-3 mr-1" />
                            Permissions
                          </Button>
                          {(u.status ?? 'approved') !== 'rejected' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleActive(u)}
                              disabled={updatingUserId === u.user_id}
                              className={`h-8 text-xs ${(u.is_active ?? true) ? 'text-amber-700 border-amber-200 hover:bg-amber-50' : 'text-green-700 border-green-200 hover:bg-green-50'}`}
                              data-testid={`toggle-active-${u.user_id}`}
                              title={(u.is_active ?? true) ? 'Disable account' : 'Enable account'}
                            >
                              <Power className="w-3 h-3 mr-1" />
                              {(u.is_active ?? true) ? 'Disable' : 'Enable'}
                            </Button>
                          )}
                          {(u.status ?? 'approved') === 'rejected' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openApprovalModal(u)}
                              className="h-8 text-xs text-green-700 border-green-200 hover:bg-green-50"
                              data-testid={`reapprove-user-${u.user_id}`}
                            >
                              <ShieldCheck className="w-3 h-3 mr-1" />
                              Re-approve
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeletingUser(u)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                      {u.user_id === user?.user_id && (
                        <span className="text-xs text-slate-400">Current user</span>
                      )}
                    </td>
                  </tr>
                ))}
                {users.filter(u => (u.status ?? 'approved') !== 'pending').length === 0 && (
                  <tr>
                    <td colSpan={6} className="table-cell text-center text-slate-500 py-8">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="sm:hidden p-4 space-y-3">
            {users.filter(u => (u.status ?? 'approved') !== 'pending').map((u) => (
              <div
                key={u.user_id}
                className="bg-white border border-slate-200 rounded-lg p-4"
                data-testid={`user-card-${u.user_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-base font-semibold text-slate-600">
                          {u.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{u.name}</p>
                      <p className="text-sm text-slate-500">{u.email}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border capitalize ${getRoleBadgeClass(u.role)}`}>
                    {u.role}
                  </span>
                </div>

                {u.user_id !== user?.user_id && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                        Change Role
                      </label>
                      <Select
                        value={u.role}
                        onValueChange={(value) => handleRoleChange(u.user_id, value)}
                        disabled={updatingUserId === u.user_id}
                      >
                        <SelectTrigger className="h-12 w-full" data-testid={`mobile-role-select-${u.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="production">Production</SelectItem>
                          <SelectItem value="lab">Lab</SelectItem>
                          <SelectItem value="accounts">Accounts</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => openPermissionsEditor(u)}
                        className="flex-1 h-12"
                        data-testid={`mobile-edit-permissions-${u.user_id}`}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Permissions
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setDeletingUser(u)}
                        className="h-12 px-4 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        data-testid={`mobile-delete-user-${u.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {users.length === 0 && (
              <div className="text-center text-slate-500 py-8">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No users found</p>
              </div>
            )}
          </div>
        </div>

        {/* Permissions Editor Modal */}
        {editingPermissions && editingUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden" data-testid="permissions-modal">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Edit Permissions</h2>
                  <p className="text-sm text-slate-500">{editingUser.name} ({editingUser.role})</p>
                </div>
                <button
                  onClick={closePermissionsEditor}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-slate-600">Select which features this user can access:</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resetToDefault(editingUser.role)}
                    className="text-xs"
                  >
                    Reset to Default
                  </Button>
                </div>

                <div className="space-y-3">
                  {allPermissions.map((perm) => (
                    <label
                      key={perm.id}
                      className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <Checkbox
                        checked={tempPermissions.includes(perm.id)}
                        onCheckedChange={() => togglePermission(perm.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 text-sm">{perm.label}</p>
                        <p className="text-xs text-slate-500">{perm.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
                <Button
                  variant="outline"
                  onClick={closePermissionsEditor}
                >
                  Cancel
                </Button>
                <Button
                  onClick={savePermissions}
                  disabled={updatingUserId === editingPermissions}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save Permissions
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add User Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-testid="add-user-modal">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-slate-600" />
                  </div>
                  <h2 className="text-lg font-bold text-slate-900">Add New User</h2>
                </div>
                <button
                  onClick={() => { setShowAddModal(false); setNewUserData({ name: '', email: '', role: 'production' }); }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <form onSubmit={handleAddUser} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={newUserData.name}
                    onChange={(e) => setNewUserData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Enter full name"
                    className="h-12"
                    required
                    data-testid="new-user-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData(p => ({ ...p, email: e.target.value }))}
                    placeholder="Enter email address"
                    className="h-12"
                    required
                    data-testid="new-user-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <Select value={newUserData.role} onValueChange={(v) => setNewUserData(p => ({ ...p, role: v }))}>
                    <SelectTrigger className="h-12" data-testid="new-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="production">Production</SelectItem>
                      <SelectItem value="lab">Lab</SelectItem>
                      <SelectItem value="accounts">Accounts</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                  Note: The user will be able to login using Google with this email address.
                </p>
              </form>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
                <Button
                  variant="outline"
                  onClick={() => { setShowAddModal(false); setNewUserData({ name: '', email: '', role: 'production' }); }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddUser}
                  disabled={addingUser || !newUserData.name.trim() || !newUserData.email.trim()}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="submit-add-user"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Confirmation Modal */}
        {deletingUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-testid="delete-user-modal">
              <div className="p-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900 text-center mb-2">Delete User</h2>
                <p className="text-slate-500 text-center">
                  Are you sure you want to delete <strong>{deletingUser.name}</strong>?
                </p>
                <p className="text-sm text-slate-400 text-center mt-1">
                  {deletingUser.email}
                </p>
                <p className="text-sm text-amber-600 bg-amber-50 rounded-lg p-3 mt-4 text-center">
                  This action cannot be undone. The user will lose access immediately.
                </p>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
                <Button variant="outline" onClick={() => setDeletingUser(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteUser}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-user"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete User
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Approve User Modal */}
        {approvingUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-testid="approve-user-modal">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Approve User</h2>
                  <p className="text-sm text-slate-500 truncate">
                    {approvingUser.name} · {approvingUser.email}
                  </p>
                </div>
                <button
                  onClick={() => setApprovingUser(null)}
                  className="p-1 hover:bg-slate-100 rounded"
                  data-testid="close-approve-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Role
                  </label>
                  <Select value={approvalRole} onValueChange={setApprovalRole}>
                    <SelectTrigger data-testid="approval-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="production">Production</SelectItem>
                      <SelectItem value="lab">Lab</SelectItem>
                      <SelectItem value="accounts">Accounts</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-slate-500" />
                    <label className="text-sm font-medium text-slate-700">
                      Assign Companies
                    </label>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    Select the companies this user can access. Leave all unchecked to allow access to all companies.
                  </p>
                  <div className="space-y-2 border border-slate-200 rounded-md p-3 max-h-48 overflow-y-auto">
                    {companies.length === 0 ? (
                      <p className="text-xs text-slate-400">No companies found</p>
                    ) : companies.map(c => (
                      <label
                        key={c.id}
                        className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"
                        data-testid={`approval-company-${c.id}`}
                      >
                        <Checkbox
                          checked={approvalCompanyIds.includes(c.id)}
                          onCheckedChange={() => toggleApprovalCompany(c.id)}
                        />
                        <span>{c.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setApprovingUser(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleApproveUser}
                  disabled={approvalSubmitting}
                  className="bg-green-600 hover:bg-green-700 text-white gap-1"
                  data-testid="confirm-approve-user"
                >
                  <ShieldCheck className="w-4 h-4" />
                  {approvalSubmitting ? 'Approving...' : 'Approve'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
